import { Lane, LanePath } from './simulationTypes';

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/riftmap`;

/* ══════════════════ Fallback (hardcoded) ══════════════════ */

const FALLBACK_PATHS: Record<Lane, LanePath> = {
  top: {
    waypoints: [
      { x: 42, y: 425 }, { x: 35, y: 360 }, { x: 30, y: 280 },
      { x: 28, y: 200 }, { x: 28, y: 130 }, { x: 35, y: 72 },
      { x: 55, y: 48 }, { x: 140, y: 38 }, { x: 240, y: 35 },
      { x: 340, y: 35 }, { x: 420, y: 38 }, { x: 455, y: 52 },
      { x: 468, y: 72 },
    ],
  },
  mid: {
    waypoints: [
      { x: 68, y: 448 }, { x: 105, y: 410 }, { x: 148, y: 368 },
      { x: 195, y: 320 }, { x: 245, y: 270 }, { x: 268, y: 248 },
      { x: 315, y: 195 }, { x: 365, y: 148 }, { x: 410, y: 105 },
      { x: 448, y: 68 },
    ],
  },
  bot: {
    waypoints: [
      { x: 68, y: 465 }, { x: 140, y: 478 }, { x: 240, y: 482 },
      { x: 340, y: 482 }, { x: 420, y: 478 }, { x: 458, y: 455 },
      { x: 475, y: 380 }, { x: 480, y: 280 }, { x: 480, y: 200 },
      { x: 478, y: 130 }, { x: 468, y: 72 },
    ],
  },
};

/* ══════════════════ Fetch from backend ══════════════════ */

/**
 * Fetch lane paths from the backend.
 * The backend returns saved user-defined paths, or auto-generates
 * them using chained A* pathfinding along each lane.
 */
export async function fetchLanePaths(): Promise<Record<Lane, LanePath>> {
  try {
    const res = await fetch(`${API_BASE}/lane-paths`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    const result: Record<Lane, LanePath> = {
      top: { waypoints: [] },
      mid: { waypoints: [] },
      bot: { waypoints: [] },
    };

    for (const lane of ['top', 'mid', 'bot'] as Lane[]) {
      const laneData = data[lane];
      if (laneData?.waypoints?.length >= 2) {
        result[lane] = { waypoints: laneData.waypoints };
      } else {
        console.warn(`[lanePaths] No path for ${lane}, using fallback`);
        result[lane] = FALLBACK_PATHS[lane];
      }
    }

    console.log(
      `[lanePaths] Loaded — top: ${result.top.waypoints.length} pts (${data.top?.source ?? '?'}), ` +
      `mid: ${result.mid.waypoints.length} pts (${data.mid?.source ?? '?'}), ` +
      `bot: ${result.bot.waypoints.length} pts (${data.bot?.source ?? '?'})`,
    );
    return result;
  } catch (err) {
    console.warn('[lanePaths] API fetch failed, using fallback paths:', err);
    return { ...FALLBACK_PATHS };
  }
}

/** Synchronous access — used by simulation logic after paths are loaded */
export let LANE_PATHS: Record<Lane, LanePath> = { ...FALLBACK_PATHS };

/** Call once on mount to load real paths */
export async function initLanePaths(): Promise<void> {
  LANE_PATHS = await fetchLanePaths();
}
